import React from 'react';
import Box from '@nubank/nuds-web/components/Box/Box';
import styled, { css } from 'styled-components';
import Image from '@nubank/nuds-web/components/Image/Image';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Link from '@nubank/nuds-web/components/Link/Link';
import Grid from '@nubank/nuds-web/components/Grid/Grid';
import { breakpointsMedia, useBreakpointsMediaUp } from '@nubank/nuds-web/styles/breakpoints';
import { useParallax } from 'react-scroll-parallax';

const StyledRowContainer = styled.div`
  align-items: center;
  background-color: #f8f8fa;
  border-radius: 12px;
  display: inline-flex;
  gap: 20px;
  justify-content: left;
  position: relative;
  width: 100%;
  margin-top: 15px;
  ${breakpointsMedia({
    xs: css`
      padding: 30px;
    `,
    md: css`
      padding: 30px 80px;
    `,
    lg: css`
      padding: 30px;
    `,
  })}
`;

const StyledIconAndTitleContainer = styled.div`
  position: relative;
  background-color: #f8f8fa;
  border-radius: 12px;
  padding-left: 31px;
  padding-right: 31px;
  padding-bottom: 40px;

  h3 {
    ${breakpointsMedia({
    xs: css`
      font-size: 27px;
    `,
    md: css`
      font-size: 28px;
    `,
    lg: css`
      font-size: 26px;
    `,
    xl: css`
      font-size: 30px;
    `,
  })}
  }

  span {
    /* stylelint-disable property-no-vendor-prefix, property-no-unknown */
    background-image: linear-gradient(137.07deg, #9F2DEB 42.83%, #6200A3 86.85%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

const StyledCardBackgroundImagesContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 17px;
  justify-content: center;
  max-width: 1391px;
  margin: 0 auto;
`;

const StyledTitleIcon = styled(Image)`
  position: absolute;
  margin-top: -50px;

  ${breakpointsMedia({
    md: css`
      left: 12%;
    `,
    lg: css`
      left: 17%;
      margin-top: -80px;
    `,
  })}

`;

const StyledListContainer = styled(Box)`
  ${breakpointsMedia({
    lg: css`
      gap: 20px;
    `,
  })}
`;

function CreditoMGM() {
  const { ref } = useParallax({ speed: 20, easing: [0, 1, 1, -0.3], disabled: useBreakpointsMediaUp('lg') });
  return (
    <Box
      tag="section"
      height="100%"
      width="100%"
      marginBottom={{ xs: '32px', lg: '100px' }}
      paddingHorizontal={{ xs: '0', lg: '56px', xl: '20x' }}

    >
      <Grid
        paddingLeft={{ xs: '0', lg: '0x' }}
        paddingRight={{ xs: '0', lg: '0' }}
      >

        {/* CONTENT BOX */}

        <Grid.Child
          gridColumn={{ xs: '1 / span 4', md: '2 / span 4', lg: '2 / span 10' }}
          gridRow="1"
        >
          {/* CARD BACKGROUND IMAGES */}

          <StyledCardBackgroundImagesContainer
            ref={ref}
          >
            <Image
              alt="Nubank"
              srcSet={{
                xs: 'mgm/credito/card-background-left-xs.jpg',
                lg: 'mgm/credito/card-background-left-lg.jpg',
              }}
              loading="lazy"
            />
            <Image
              alt="Nubank"
              srcSet={{
                xs: 'mgm/credito/card-background-right-xs.jpg',
                lg: 'mgm/credito/card-background-right-lg.jpg',
              }}
              loading="lazy"
            />
          </StyledCardBackgroundImagesContainer>

          {/* CONTENT ROWS */}

          <Box
            position="relative"
            marginTop={{ xs: '-70px', md: '-20px', lg: '-50px' }}
            maxWidth={{ xs: '100%', lg: '1215px' }}
            paddingHorizontal={{ xs: '32px', lg: '12x' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            margin="0 auto"
          >
            <StyledIconAndTitleContainer>
              <StyledTitleIcon
                webp={false}
                alt="Nu"
                src="mgm/credito/card-new.svg"
                width={{ xs: '100px', md: '80px', lg: '100px' }}
                loading="lazy"
              />
              <Typography
                strong
                variant="heading3"
                height="100%"
                paddingTop={{ xs: '70px', md: '50px', lg: '40px' }}
                paddingHorizontal={{ xs: '0', md: '12x', lg: '8x' }}
                whiteSpace={{ xs: 'pre-line', lg: 'initial' }}
                textAlign={{ xs: 'left', lg: 'center' }}
              >
                La
                <span> tarjeta de crédito </span>
                sin anualidad ni cobros sorpresa
              </Typography>
            </StyledIconAndTitleContainer>

            <StyledListContainer
              display="flex"
              flexDirection={{ xs: 'column', lg: 'row' }}
              marginTop={{ xs: '0', lg: '24px' }}
            >
              <StyledRowContainer>
                <Image
                  alt="Sin anualidad"
                  src="mgm/credito/icon-1.svg"
                  webp={false}
                  width="49px"
                  maxWidth="49px"
                  loading="lazy"
                />
                <span>
                  <Typography strong variant="paragraph2" tag="span">
                    Adelanta MSI de tus compras y obtén un descuento
                  </Typography>
                  <Typography variant="paragraph2" tag="span">
                    {' '}
                    por adelantar tus pagos.
                  </Typography>
                </span>
              </StyledRowContainer>

              <StyledRowContainer>
                <Image
                  alt="Sin penalizaciones por uso mínimo"
                  src="mgm/credito/icon-2.svg"
                  webp={false}
                  width="49px"
                  maxWidth="49px"
                  loading="lazy"
                />
                <span>
                  <Typography strong variant="paragraph2" tag="span">
                    Con Compras Diferidas y Plan de Pagos Fijos,
                  </Typography>
                  <Typography variant="paragraph2" tag="span">
                    {' '}
                    tú eliges cómo pagar desde la app.
                  </Typography>
                </span>
              </StyledRowContainer>

              <StyledRowContainer>
                <Image
                  alt="Sin cobros por reposición si pierdes o roban tu Nu"
                  src="mgm/credito/icon-3.svg"
                  webp={false}
                  width="49px"
                  maxWidth="49px"
                  loading="lazy"
                />
                <span>
                  <Typography variant="paragraph2" tag="span">
                    Con uso frecuente y pagos puntuales,
                  </Typography>
                  <Typography strong variant="paragraph2" tag="span">
                    {' '}
                    tu límite de crédito podría aumentar.
                  </Typography>
                </span>
              </StyledRowContainer>
            </StyledListContainer>

          </Box>

          {/* LINK */}

          <Box
            position="relative"
            maxWidth={{ xs: '100%' }}
            paddingHorizontal={{ xs: '32px', lg: '0' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            marginTop="8x"
          >
            <Link
              intlKey="MGM.CREDITO.LINK"
              href="/credito"
              variant="action"
              color="primary"
              alignSelf="center"
            />
          </Box>
        </Grid.Child>

      </Grid>
    </Box>
  );
}

export default CreditoMGM;
